import { useState } from "react";
import {
  Choices, Details, Disclaimer, Nav, Panel, Progress, Question,
  ResultBlock, ResultFooter, ResultHeadline, ResultList, ToolFrame, useTool, useToolEvents, WorkableOptions,
} from "@/components/site/ToolShell";

const SLUG = "sleep-compatibility-selector";

const NEEDS = [
  { value: "temperature", label: "One sleeper runs hotter or colder" },
  { value: "firmness", label: "Different firmness preferences" },
  { value: "motion", label: "Movement wakes one sleeper" },
  { value: "elevation", label: "One or both sleepers want independent elevation" },
];

function evaluate(f) {
  const n = f.needs || [];
  const has = (k) => n.includes(k);
  const keep = f.mattress;

  const splitNeeded = has("firmness") || has("elevation");
  const dualTemp = has("temperature");

  let headline, direction;
  if (splitNeeded && dualTemp) {
    direction = "Split adjustable sleep system with independent temperature control";
    headline = "A split configuration with independent temperature control fits what you described.";
  } else if (splitNeeded) {
    direction = "Split configuration — independent firmness or elevation per side";
    headline = "A split configuration can address these needs independently on each side of the bed.";
  } else if (dualTemp) {
    direction = "Dual-zone temperature control over your existing sleep surface";
    headline = "Independent temperature control is the part worth solving first.";
  } else if (has("motion")) {
    direction = "Motion-isolating mattress construction";
    headline = "Motion isolation is the main thing to solve here.";
  } else {
    direction = "No specific conflict selected yet";
    headline = "Tell us what you are trying to solve and we can point you in a direction.";
  }

  const reasons = [];
  if (has("firmness")) reasons.push("Different firmness preferences need genuinely separate sleep surfaces, not a compromise firmness.");
  if (has("elevation")) reasons.push("Independent elevation requires a base that articulates each side separately.");
  if (dualTemp) reasons.push("Temperature difference between sleepers is only solved by a system offering dual-zone or independent-side control.");
  if (has("motion")) reasons.push("Motion transfer is a property of the mattress construction, so it is addressed at the sleep surface.");

  const checks = [];
  if (splitNeeded && keep === "Keep it")
    checks.push("A split configuration usually means two separate sleep surfaces, which is difficult to achieve while keeping a single existing mattress. Confirm what is possible before committing.");
  if (keep === "Keep it")
    checks.push("Confirm any base or topper is compatible with your existing mattress's thickness, construction and warranty — some warranties are voided by adjustable bases.");
  if (keep === "Not sure")
    checks.push("Whether you keep your mattress changes the options substantially. Measuring its thickness and checking its warranty terms is a useful next step.");
  if (has("elevation"))
    checks.push("Only claim independent positioning for a base that actually articulates each side separately — verify this on the specific model.");
  if (dualTemp)
    checks.push("Independent temperature control requires a system that genuinely provides dual-zone or independent-side control. Many products cool or warm the whole bed as one zone.");
  checks.push("Confirm your bed frame, headboard and room clearances accommodate the base you choose.");

  // Configurations derived from the direction already established above.
  const workable = [];
  if (splitNeeded && dualTemp) {
    workable.push("A split adjustable base so firmness or elevation is set independently on each side");
    workable.push("A sleep system with genuine dual-zone or independent-side temperature control");
  } else if (splitNeeded) {
    workable.push("A split adjustable base so each side is set independently");
    workable.push(has("elevation") ? "A base that articulates each side separately" : "Two separate sleep surfaces rather than a compromise firmness");
  } else if (dualTemp) {
    workable.push("A dual-zone temperature layer over your existing sleep surface");
    workable.push("A sleep system with independent-side temperature control");
  } else if (has("motion")) {
    workable.push("A mattress whose construction isolates motion between sides");
  } else {
    workable.push("Tell us which conflict you are solving and we can show the configurations that address it");
  }
  if (has("motion") && (splitNeeded || dualTemp))
    workable.push("Motion isolation handled at the sleep surface rather than at the base");
  if (keep === "Keep it")
    workable.push("Options compatible with keeping your current mattress, once its thickness and warranty are confirmed");
  else if (keep === "Open to replacing it")
    workable.push("Complete systems, since you are open to replacing the mattress");

  return { headline, direction, reasons, checks, needCount: n.length, workable,
    optionsHeadline: n.length ? "Configurations that address what you described" : "What works for you" };
}

const INITIAL = { needs: [], mattress: "Not sure" };

export default function SleepSelector() {
  const tool = useTool(SLUG);
  const { start, complete, resetRun } = useToolEvents(SLUG);
  const [step, setStep] = useState(0);
  const [f, setF] = useState(INITIAL);
  const [result, setResult] = useState(null);
  const [budget, setBudget] = useState(null);
  const set = (k) => (v) => { start(); setF((s) => ({ ...s, [k]: v })); };
  const total = 2;

  const stepValid = step === 0 ? (f.needs || []).length > 0 : true;

  const next = () => {
    start();
    if (step < total - 1) return setStep(step + 1);
    const r = evaluate(f);
    setResult(r);
    complete({ direction: r.direction });
  };

  if (!tool) return <div className="h-[60vh]" />;

  return (
    <ToolFrame tool={tool}>
      <Panel>
        {!result ? (
          <>
            <Progress step={step} total={total} />

            {step === 0 && (
              <Question label="What are you trying to solve?" hint="Select all that apply.">
                <Choices testId="choice-needs" cols={1} multi options={NEEDS} value={f.needs} onChange={set("needs")} />
              </Question>
            )}

            {step === 1 && (
              <Question label="Do you want to keep your current mattress?">
                <Choices
                  testId="choice-mattress"
                  cols={1}
                  options={[
                    { value: "Keep it", label: "Keep it" },
                    { value: "Open to replacing it", label: "Open to replacing it" },
                    { value: "Not sure", label: "Not sure" },
                  ]}
                  value={f.mattress}
                  onChange={set("mattress")}
                />
              </Question>
            )}

            <Nav step={step} total={total} onBack={() => setStep(Math.max(0, step - 1))} onNext={next} disabled={!stepValid} />
          </>
        ) : (
          <div data-testid="tool-result">
            <ResultHeadline
              eyebrow="Your best-fit direction"
              headline={result.headline}
              reasons={result.reasons.slice(0, 3)}
              testId="result-headline"
            />
            <WorkableOptions
              tool={tool}
              headline={result.optionsHeadline}
              options={result.workable}
              productPath="Adjustable bases, split configurations and temperature-regulating sleep systems"
            />
            <div className="border-t-2 border-charcoal mt-12">
              <ResultBlock label="Best-fit direction" value={result.direction} emphasis testId="result-direction" />
              <Details label="What to check before buying" testId="result-details">
                <ResultList label="Compatibility checks" items={result.checks} testId="result-checks" />
              </Details>
              <div className="py-5 border-b border-line" data-testid="result-refine-budget">
                <div className="text-[11px] tracking-[0.16em] uppercase text-[#4F4B45] font-medium mb-3">
                  Optional — refine by budget
                </div>
                <Choices
                  testId="choice-budget"
                  options={["Entry scope", "Mid scope", "Premium scope", "Not sure yet"]}
                  value={budget}
                  onChange={setBudget}
                />
              </div>
            </div>

            <Disclaimer>
              A configuration addresses the specific needs you selected — it does not resolve every
              sleep problem. Independent elevation and independent temperature control only apply to
              systems that actually provide them, so verify both on the specific model.
            </Disclaimer>

            <div className="mt-2">
              <ResultFooter onReset={() => { resetRun(); setResult(null); setStep(0); setF(INITIAL); setBudget(null); }} />
            </div>
          </div>
        )}
      </Panel>
    </ToolFrame>
  );
}
